import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import BeatLoader from "react-spinners/BeatLoader";
import TablePagination from "@mui/material/TablePagination";
import { toast } from "react-toastify";
import { MyContext } from "../../contexts/ContextProvider";
import SUB_ACCOUNT_PAYOUT_STATUSES from "../../constants/sub-account-payout-statuses";
import {
  portalCreateSubAccount,
  portalDeleteSubAccount,
  portalListSubAccountPayouts,
  portalListSubAccounts,
  portalUpdateSubAccount,
} from "../../services/merchant-portal";
import "./portal.scss";

const createSchema = Yup.object({
  name: Yup.string().trim().required("Name is required").max(128),
  phone_number: Yup.string().trim().required("Phone is required").max(32),
  currency: Yup.string().trim().required("Currency is required").max(5),
  split_percentage: Yup.number()
    .typeError("Enter a percentage")
    .min(0.01, "Minimum 0.01%")
    .max(100, "Maximum 100%")
    .required("Split is required"),
  reference: Yup.string().trim().max(128),
  is_active: Yup.boolean(),
});

const editSchema = Yup.object({
  name: Yup.string().trim().required("Name is required").max(128),
  phone_number: Yup.string().trim().required("Phone is required").max(32),
  split_percentage: Yup.number()
    .typeError("Enter a percentage")
    .min(0.01, "Minimum 0.01%")
    .max(100, "Maximum 100%")
    .required("Split is required"),
  reference: Yup.string().trim().max(128),
  is_active: Yup.boolean(),
});

const emptyCreate = {
  name: "",
  phone_number: "",
  currency: "",
  split_percentage: "",
  reference: "",
  is_active: true,
};

const MerchantPortalSubAccounts = () => {
  const { currencyCode } = useContext(MyContext);
  const currencyOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    (currencyCode || []).forEach((item) => {
      const value = (item?.code || item?.name || "").trim().toUpperCase();
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ value, label: value });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [currencyCode]);

  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [accountsPage, setAccountsPage] = useState(0);
  const [accountsRows, setAccountsRows] = useState(20);
  const [activeFilter, setActiveFilter] = useState("");
  const [accountsLoading, setAccountsLoading] = useState(true);

  const [payouts, setPayouts] = useState([]);
  const [payoutsTotal, setPayoutsTotal] = useState(0);
  const [payoutsPage, setPayoutsPage] = useState(0);
  const [payoutsRows, setPayoutsRows] = useState(20);
  const [payoutStatus, setPayoutStatus] = useState("");
  const [payoutSubAccountId, setPayoutSubAccountId] = useState("");
  const [payoutsLoading, setPayoutsLoading] = useState(false);

  const [accountOptions, setAccountOptions] = useState([]);
  const [modalMode, setModalMode] = useState(null);
  const [editing, setEditing] = useState(null);

  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const query = {
        page: accountsPage + 1,
        size: accountsRows,
      };
      if (activeFilter === "true" || activeFilter === "false") {
        query.active = activeFilter;
      }
      const res = await portalListSubAccounts(query);
      if (res.status_code === 200) {
        setAccounts(res.data?.items || []);
        setAccountsTotal(res.data?.total ?? 0);
      } else {
        toast.error(res.message || "Could not load sub accounts");
      }
    } catch (err) {
      toast.error(`${err}`);
    } finally {
      setAccountsLoading(false);
    }
  }, [accountsPage, accountsRows, activeFilter]);

  const loadPayouts = useCallback(async () => {
    setPayoutsLoading(true);
    try {
      const res = await portalListSubAccountPayouts({
        page: payoutsPage + 1,
        size: payoutsRows,
        status: payoutStatus,
        sub_account_id: payoutSubAccountId,
      });
      if (res.status_code === 200) {
        setPayouts(res.data?.items || []);
        setPayoutsTotal(res.data?.total ?? 0);
      } else {
        toast.error(res.message || "Could not load payouts");
      }
    } catch (err) {
      toast.error(`${err}`);
    } finally {
      setPayoutsLoading(false);
    }
  }, [payoutsPage, payoutsRows, payoutStatus, payoutSubAccountId]);

  useEffect(() => {
    if (tab === "accounts") loadAccounts();
  }, [tab, loadAccounts]);

  useEffect(() => {
    if (tab === "payouts") {
      loadPayouts();
      portalListSubAccounts({ page: 1, size: 100 })
        .then((res) => {
          if (res.status_code === 200) {
            setAccountOptions(res.data?.items || []);
          }
        })
        .catch(() => {});
    }
  }, [tab, loadPayouts]);

  const openCreate = () => {
    setEditing(null);
    setModalMode("create");
  };

  const openEdit = (row) => {
    setEditing(row);
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditing(null);
  };

  const handleDelete = (row) => {
    if (
      !window.confirm(
        `Delete sub account "${row.name}"? Existing payouts will still complete.`,
      )
    ) {
      return;
    }
    portalDeleteSubAccount(row.id)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(res.message || "Deleted");
          loadAccounts();
        } else {
          toast.error(res.message || "Delete failed");
        }
      })
      .catch((err) => toast.error(`${err}`));
  };

  const editInitial = editing
    ? {
        name: editing.name || "",
        phone_number: editing.phone_number || "",
        split_percentage: editing.split_percentage ?? "",
        reference: editing.reference || "",
        is_active: Boolean(editing.is_active),
      }
    : emptyCreate;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-semibold">Sub accounts</h1>
        {tab === "accounts" ? (
          <button type="button" className="portal-btn" onClick={openCreate}>
            Add sub account
          </button>
        ) : null}
      </div>

      <p className="portal-muted mb-4 max-w-3xl">
        Sub accounts receive a share of direct-pay collections in a matching
        currency. Pass the sub account id or reference on direct-pay requests
        to route splits.
      </p>

      <div className="portal-tabs mb-4">
        <button
          type="button"
          className={`portal-tab${tab === "accounts" ? " portal-tab--active" : ""}`}
          onClick={() => setTab("accounts")}
        >
          Accounts
        </button>
        <button
          type="button"
          className={`portal-tab${tab === "payouts" ? " portal-tab--active" : ""}`}
          onClick={() => setTab("payouts")}
        >
          Payouts
        </button>
      </div>

      {tab === "accounts" ? (
        <>
          <div className="portal-card portal-form mb-4 flex flex-wrap gap-3 items-end">
            <div className="field mb-0 min-w-[160px]">
              <label htmlFor="active-filter">Status</label>
              <select
                id="active-filter"
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setAccountsPage(0);
                }}
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="portal-card overflow-x-auto">
            {accountsLoading ? (
              <div className="flex justify-center py-12">
                <BeatLoader color="#03C9D7" />
              </div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left opacity-75 border-b border-gray-200 dark:border-gray-700">
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Reference</th>
                      <th className="py-2 pr-3">Phone</th>
                      <th className="py-2 pr-3">Currency</th>
                      <th className="py-2 pr-3">Split %</th>
                      <th className="py-2 pr-3">Active</th>
                      <th className="py-2 pr-3">Id</th>
                      <th className="py-2 pr-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-gray-100 dark:border-gray-800"
                      >
                        <td className="py-2 pr-3">{row.name}</td>
                        <td className="py-2 pr-3 font-mono text-xs">
                          {row.reference || "—"}
                        </td>
                        <td className="py-2 pr-3">{row.phone_number}</td>
                        <td className="py-2 pr-3">{row.currency}</td>
                        <td className="py-2 pr-3">{row.split_percentage}</td>
                        <td className="py-2 pr-3">
                          {row.is_active ? "Yes" : "No"}
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs break-all">
                          {row.id}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          <button
                            type="button"
                            className="text-[#03C9D7] text-sm mr-3"
                            onClick={() => openEdit(row)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-red-500 text-sm"
                            onClick={() => handleDelete(row)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!accounts.length ? (
                  <p className="portal-muted py-6 text-center">
                    No sub accounts yet.
                  </p>
                ) : null}
                <TablePagination
                  component="div"
                  count={accountsTotal}
                  page={accountsPage}
                  onPageChange={(_, p) => setAccountsPage(p)}
                  rowsPerPage={accountsRows}
                  onRowsPerPageChange={(e) => {
                    setAccountsRows(parseInt(e.target.value, 10));
                    setAccountsPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50]}
                />
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="portal-card portal-form mb-4 grid md:grid-cols-3 gap-3">
            <div className="field mb-0">
              <label htmlFor="payout-status">Payout status</label>
              <select
                id="payout-status"
                value={payoutStatus}
                onChange={(e) => {
                  setPayoutStatus(e.target.value);
                  setPayoutsPage(0);
                }}
              >
                <option value="">All</option>
                {SUB_ACCOUNT_PAYOUT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="field mb-0">
              <label htmlFor="payout-sub">Sub account</label>
              <select
                id="payout-sub"
                value={payoutSubAccountId}
                onChange={(e) => {
                  setPayoutSubAccountId(e.target.value);
                  setPayoutsPage(0);
                }}
              >
                <option value="">All</option>
                {accountOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.reference ? ` (${a.reference})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="portal-card overflow-x-auto">
            {payoutsLoading ? (
              <div className="flex justify-center py-12">
                <BeatLoader color="#03C9D7" />
              </div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left opacity-75 border-b">
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Sub account</th>
                      <th className="py-2 pr-3">Parent ref</th>
                      <th className="py-2 pr-3">Amount</th>
                      <th className="py-2 pr-3">Fee</th>
                      <th className="py-2 pr-3">Phone</th>
                      <th className="py-2 pr-3">Paid at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((p) => (
                      <tr
                        key={p.payout_id}
                        className="border-b border-gray-100 dark:border-gray-800"
                      >
                        <td className="py-2 pr-3">{p.status}</td>
                        <td className="py-2 pr-3">
                          {p.sub_account_reference || p.sub_account_id}
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs">
                          {p.parent_external_reference_id || p.parent_reference}
                        </td>
                        <td className="py-2 pr-3">
                          {p.amount} {p.currency}
                        </td>
                        <td className="py-2 pr-3">{p.fee}</td>
                        <td className="py-2 pr-3">{p.phone_masked}</td>
                        <td className="py-2 pr-3">
                          {p.paid_at ? String(p.paid_at) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!payouts.length ? (
                  <p className="portal-muted py-6 text-center">No payouts found.</p>
                ) : null}
                <TablePagination
                  component="div"
                  count={payoutsTotal}
                  page={payoutsPage}
                  onPageChange={(_, p) => setPayoutsPage(p)}
                  rowsPerPage={payoutsRows}
                  onRowsPerPageChange={(e) => {
                    setPayoutsRows(parseInt(e.target.value, 10));
                    setPayoutsPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50]}
                />
              </>
            )}
          </div>
        </>
      )}

      {modalMode ? (
        <div className="portal-modal-backdrop">
          <div className="portal-card portal-form max-w-lg w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">
              {modalMode === "create" ? "New sub account" : "Edit sub account"}
            </h2>
            <Formik
              enableReinitialize
              initialValues={editInitial}
              validationSchema={modalMode === "create" ? createSchema : editSchema}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  const payload =
                    modalMode === "create"
                      ? {
                          name: values.name.trim(),
                          phone_number: values.phone_number.trim(),
                          currency: values.currency.trim().toUpperCase(),
                          split_percentage: Number(values.split_percentage),
                          reference: values.reference?.trim() || undefined,
                          is_active: values.is_active,
                        }
                      : {
                          name: values.name.trim(),
                          phone_number: values.phone_number.trim(),
                          split_percentage: Number(values.split_percentage),
                          reference: values.reference?.trim() || undefined,
                          is_active: values.is_active,
                        };
                  const res =
                    modalMode === "create"
                      ? await portalCreateSubAccount(payload)
                      : await portalUpdateSubAccount(editing.id, payload);
                  if (res.status_code === 200) {
                    toast.success(res.message || "Saved");
                    closeModal();
                    loadAccounts();
                  } else {
                    toast.error(res.message || "Save failed");
                  }
                } catch (err) {
                  toast.error(`${err}`);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                handleSubmit,
                handleChange,
                values,
                errors,
                touched,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className="field">
                    <label htmlFor="name">Name</label>
                    <input id="name" name="name" value={values.name} onChange={handleChange} />
                    {errors.name && touched.name ? (
                      <div className="error">{errors.name}</div>
                    ) : null}
                  </div>
                  <div className="field">
                    <label htmlFor="phone_number">Payout phone</label>
                    <input
                      id="phone_number"
                      name="phone_number"
                      value={values.phone_number}
                      onChange={handleChange}
                    />
                    {errors.phone_number && touched.phone_number ? (
                      <div className="error">{errors.phone_number}</div>
                    ) : null}
                  </div>
                  {modalMode === "create" ? (
                    <div className="field">
                      <label htmlFor="currency">Currency</label>
                      <select
                        id="currency"
                        name="currency"
                        value={values.currency}
                        onChange={handleChange}
                      >
                        <option value="">Select currency</option>
                        {currencyOptions.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      {errors.currency && touched.currency ? (
                        <div className="error">{errors.currency}</div>
                      ) : null}
                    </div>
                  ) : (
                    <p className="portal-muted text-sm mb-3">
                      Currency: <strong>{editing?.currency}</strong> (cannot be
                      changed)
                    </p>
                  )}
                  <div className="field">
                    <label htmlFor="split_percentage">Split percentage</label>
                    <input
                      id="split_percentage"
                      name="split_percentage"
                      type="number"
                      step="0.01"
                      min="0.01"
                      max="100"
                      value={values.split_percentage}
                      onChange={handleChange}
                    />
                    {errors.split_percentage && touched.split_percentage ? (
                      <div className="error">{errors.split_percentage}</div>
                    ) : null}
                  </div>
                  <div className="field">
                    <label htmlFor="reference">Your reference (optional)</label>
                    <input
                      id="reference"
                      name="reference"
                      value={values.reference}
                      onChange={handleChange}
                    />
                  </div>
                  <label className="flex items-center gap-2 mb-4 text-sm">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={values.is_active}
                      onChange={handleChange}
                    />
                    Active for new collections
                  </label>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      className="portal-btn portal-btn--secondary"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="portal-btn" disabled={isSubmitting}>
                      {isSubmitting ? "Saving…" : "Save"}
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MerchantPortalSubAccounts;
